/**
 * useSharing Hook
 * Shared between web and mobile for parlay sharing (send/receive/unshare/mark viewed)
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, type HttpClient } from '../api/httpClient';

// =============================================================================
// Types
// =============================================================================

export interface ShareableUser {
  id: string;
  displayName: string;
  relationship: 'following' | 'follower' | 'friend';
}

export interface SharedParlayLeg {
  player: string;
  prop: string;
  odds: string;
  playerId?: number | null;
  team?: string | null;
  opponentTeam?: string | null;
  statType?: string;
  line?: number | null;
  overUnder?: 'Over' | 'Under' | null;
  gameId?: string | null;
  gameDate?: string | null;
  marketKey?: string | null;
  sportsbook?: string | null;
  leagueKey: string;
}

export interface SharedParlay {
  parlayId: number;
  parlayName: string;
  totalOdds: string;
  result: string;
  createdAt: string;
  legs: SharedParlayLeg[];
}

export interface ShareByMe extends SharedParlay {
  recipients: Array<{
    recipientId: string;
    recipientUsername: string;
    recipientDisplayName: string | null;
    shareId: number;
    sharedAt: string;
    viewedAt: string | null;
  }>;
}

export interface ShareWithMe extends SharedParlay {
  shareId: number;
  sharedBy: {
    id: string;
    displayName: string;
  };
  sharedAt: string;
  viewedAt: string | null;
}

export interface ShareResult {
  success: boolean;
  shared: number;
  errors?: Array<{ userId: string; error: string }>;
}

export type ShareOutcome = { ok: true; message: string } | { ok: false; error: string };

/** The server answers 200 even when every recipient was refused, so success is shared > 0 */
export function shareOutcome(result: ShareResult): ShareOutcome {
  if (result.shared === 0) {
    return { ok: false, error: result.errors?.[0]?.error ?? 'Failed to share parlay' };
  }
  return {
    ok: true,
    message: `Shared with ${result.shared} ${result.shared === 1 ? 'user' : 'users'}`,
  };
}

export interface UseSharingOptions {
  httpClient: HttpClient;
  isAuthenticated: boolean;
  /** Loads shared-with-me on mount; only a consumer that shows received shares turns it on */
  autoLoadReceived?: boolean;
}

export interface UseSharingResult {
  shareableUsers: ShareableUser[];
  sharedByMe: ShareByMe[];
  sharedWithMe: ShareWithMe[];
  unviewedCount: number;
  fetchShareableUsers: () => Promise<void>;
  fetchSharedByMe: () => Promise<void>;
  fetchSharedWithMe: () => Promise<void>;
  shareParlay: (parlayId: number, userIds: string[]) => Promise<ShareResult | null>;
  unshareParlay: (parlayId: number, userId: string) => Promise<boolean>;
  markViewed: (shareId: number) => Promise<void>;
  /** One flag per list load; a share or an unshare is tracked by the component that sends it */
  shareableUsersLoading: boolean;
  sharedByMeLoading: boolean;
  sharedWithMeLoading: boolean;
  /** Each kind of request keeps its own error, so a failure shows only where that request belongs */
  shareError: string | null;
  shareableUsersError: string | null;
  /** Also holds an unshare failure, since only the Shared By Me lists unshare */
  sharedByMeError: string | null;
  sharedWithMeError: string | null;
}

// =============================================================================
// Hook
// =============================================================================

export function useSharing(options: UseSharingOptions): UseSharingResult {
  const { httpClient, isAuthenticated, autoLoadReceived = false } = options;

  const [shareableUsers, setShareableUsers] = useState<ShareableUser[]>([]);
  const [sharedByMe, setSharedByMe] = useState<ShareByMe[]>([]);
  const [sharedWithMe, setSharedWithMe] = useState<ShareWithMe[]>([]);
  const [shareableUsersLoading, setShareableUsersLoading] = useState(false);
  const [sharedByMeLoading, setSharedByMeLoading] = useState(false);
  const [sharedWithMeLoading, setSharedWithMeLoading] = useState(false);
  const [shareError, setShareError] = useState<string | null>(null);
  const [shareableUsersError, setShareableUsersError] = useState<string | null>(null);
  const [sharedByMeError, setSharedByMeError] = useState<string | null>(null);
  const [sharedWithMeError, setSharedWithMeError] = useState<string | null>(null);
  // Each list load numbers its calls, so only the newest of overlapping calls writes its state
  const shareableUsersCall = useRef(0);
  const sharedByMeCall = useRef(0);
  const sharedWithMeCall = useRef(0);
  // Share id to the viewedAt markViewed stamped, kept while shared-with-me answers still read the
  // share unviewed, so a refresh that read the row before the PATCH committed cannot count it again
  const viewedHere = useRef(new Map<number, string>());

  const unviewedCount = sharedWithMe.filter((share) => share.viewedAt === null).length;

  const fetchShareableUsers = useCallback(async () => {
    if (!isAuthenticated) return;
    const call = ++shareableUsersCall.current;
    setShareableUsersLoading(true);
    setShareableUsersError(null);

    try {
      const data = await httpClient.get<{ users: ShareableUser[] }>('/api/shares/shareable-users');
      if (call === shareableUsersCall.current) setShareableUsers(data.users || []);
    } catch (err) {
      if (call === shareableUsersCall.current) {
        setShareableUsersError(ApiError.getUserMessage(err, 'Sharing operation failed'));
      }
    } finally {
      if (call === shareableUsersCall.current) setShareableUsersLoading(false);
    }
  }, [httpClient, isAuthenticated]);

  const fetchSharedByMe = useCallback(async () => {
    if (!isAuthenticated) return;
    const call = ++sharedByMeCall.current;
    setSharedByMeLoading(true);
    setSharedByMeError(null);

    try {
      const data = await httpClient.get<{ shares: ShareByMe[] }>('/api/shares/shared-by-me');
      if (call === sharedByMeCall.current) setSharedByMe(data.shares || []);
    } catch (err) {
      if (call === sharedByMeCall.current) {
        setSharedByMeError(ApiError.getUserMessage(err, 'Sharing operation failed'));
      }
    } finally {
      if (call === sharedByMeCall.current) setSharedByMeLoading(false);
    }
  }, [httpClient, isAuthenticated]);

  const fetchSharedWithMe = useCallback(async () => {
    if (!isAuthenticated) return;
    const call = ++sharedWithMeCall.current;
    setSharedWithMeLoading(true);
    setSharedWithMeError(null);

    try {
      const data = await httpClient.get<{ shares: ShareWithMe[] }>('/api/shares/shared-with-me');
      if (call === sharedWithMeCall.current) {
        const shares = data.shares || [];
        const stillUnviewed = new Set(
          shares.filter((share) => share.viewedAt === null).map((share) => share.shareId),
        );
        // Drops the stamps this answer no longer reads as unviewed: the server's stamp wins, and an
        // unshared share keeps none
        for (const shareId of viewedHere.current.keys()) {
          if (!stillUnviewed.has(shareId)) viewedHere.current.delete(shareId);
        }
        setSharedWithMe(
          shares.map((share) => {
            const viewedAt = viewedHere.current.get(share.shareId);
            return viewedAt ? { ...share, viewedAt } : share;
          }),
        );
      }
    } catch (err) {
      if (call === sharedWithMeCall.current) {
        setSharedWithMeError(ApiError.getUserMessage(err, 'Sharing operation failed'));
      }
    } finally {
      if (call === sharedWithMeCall.current) setSharedWithMeLoading(false);
    }
  }, [httpClient, isAuthenticated]);

  const shareParlay = useCallback(
    async (parlayId: number, userIds: string[]): Promise<ShareResult | null> => {
      if (!isAuthenticated) {
        setShareError('You must be logged in to share parlays');
        return null;
      }

      setShareError(null);

      try {
        const result = await httpClient.post<ShareResult>(`/api/shares/parlay/${parlayId}`, {
          userIds,
        });
        // The share worked, so a failed refresh goes to sharedByMeError, not shareError
        await fetchSharedByMe();
        return result;
      } catch (err) {
        setShareError(ApiError.getUserMessage(err, 'Sharing operation failed'));
        return null;
      }
    },
    [httpClient, isAuthenticated, fetchSharedByMe],
  );

  const unshareParlay = useCallback(
    async (parlayId: number, userId: string): Promise<boolean> => {
      if (!isAuthenticated) {
        setSharedByMeError('You must be logged in to unshare parlays');
        return false;
      }

      setSharedByMeError(null);

      try {
        await httpClient.deleteNoContent(`/api/shares/parlay/${parlayId}/user/${userId}`);
        setSharedByMe((prev) =>
          prev
            .map((share) => {
              if (share.parlayId === parlayId) {
                return {
                  ...share,
                  recipients: share.recipients.filter((r) => r.recipientId !== userId),
                };
              }
              return share;
            })
            .filter((share) => share.recipients.length > 0),
        );
        return true;
      } catch (err) {
        setSharedByMeError(ApiError.getUserMessage(err, 'Sharing operation failed'));
        return false;
      }
    },
    [httpClient, isAuthenticated],
  );

  const markViewed = useCallback(
    async (shareId: number): Promise<void> => {
      if (!isAuthenticated) return;

      try {
        await httpClient.patch(`/api/shares/${shareId}/mark-viewed`);
        const viewedAt = new Date().toISOString();
        viewedHere.current.set(shareId, viewedAt);
        setSharedWithMe((prev) =>
          prev.map((share) => (share.shareId === shareId ? { ...share, viewedAt } : share)),
        );
      } catch (err) {
        console.error('Failed to mark share as viewed:', err);
      }
    },
    [httpClient, isAuthenticated],
  );

  // Auto-load received shares on mount
  useEffect(() => {
    if (isAuthenticated && autoLoadReceived) {
      fetchSharedWithMe();
    }
  }, [isAuthenticated, autoLoadReceived, fetchSharedWithMe]);

  return {
    shareableUsers,
    sharedByMe,
    sharedWithMe,
    unviewedCount,
    fetchShareableUsers,
    fetchSharedByMe,
    fetchSharedWithMe,
    shareParlay,
    unshareParlay,
    markViewed,
    shareableUsersLoading,
    sharedByMeLoading,
    sharedWithMeLoading,
    shareError,
    shareableUsersError,
    sharedByMeError,
    sharedWithMeError,
  };
}
